import {getDevType} from '@utils/commonUtils';
import {TEST_VARIATIONS_SEPARATOR} from '#api/services/abtest/abTestConsts';
import {getValidUpRefCookie, getUpRefUid} from '../helpers/upRefCookieHelpers';
import posthogService from './posthogService';

/**
 * @typedef {import('#api/services/abtest/abTestConsts').FrontendTestVariation} TestVariationData
 */

/** @typedef {Object<string, TestVariationData>} TestVariationsByName */

/**
 * @typedef {Object} PosthogAssignment
 * @property {string} variationName
 * @property {boolean|string} featureFlagValue
 */

class AbTestService {
	constructor() {
		this.areAssignmentsResolved = false;

		/** @type {Promise<void>|null} */
		this.assignmentsReadyPromise = null;

		/**
		 * @type {Set<string>}
		 * @private
		 */
		this.activeVariations = new Set();

		/**
		 * @type {Set<string>}
		 * @private
		 */
		this.sentImpressionTestNames = new Set();
	}

	/** @returns {Promise<void>} */
	init() {
		if (this.assignmentsReadyPromise) {
			return this.assignmentsReadyPromise;
		}

		const featureFlagsLoadPromise = posthogService.isEnabled()
			? posthogService.loadFeatureFlags()
			: Promise.resolve({'loaded': false});

		this.assignmentsReadyPromise = featureFlagsLoadPromise
			.then(({loaded}) => this._resolveAssignments(loaded));

		return this.assignmentsReadyPromise;
	}

	/** @returns {Promise<void>} */
	whenAssignmentsReady() {
		return this.init();
	}

	/**
	 * @param {!Array<string>} variationNames
	 * @returns {string}
	 */
	getActiveTestName(variationNames) {
		this.getActiveTestVariations();

		const activeVariationName = variationNames.find((variationName) => this.activeVariations.has(variationName));

		return activeVariationName ? this.getPossibleTestVariationsData()[activeVariationName].test : '';
	}

	/**
	 * @param {!Array<string>} variationNames
	 * @param {string} eventName
	 * @returns {Promise<void>}
	 */
	sendEventWhenAssignmentsReady(variationNames, eventName) {
		return this.whenAssignmentsReady()
			.then(() => this.sendEvent(this.getActiveTestName(variationNames), eventName));
	}

	/** @param {string} testNamesList */
	sendImpression(testNamesList) {
		const userId = this.getUserId();

		if (!userId || !testNamesList) {
			return;
		}

		navigator.sendBeacon(
			`abtests/impression/${this._encodePathPart(testNamesList)}/${this._encodePathPart(userId)}/`
		);
	}

	/**
	 * @param {string} testName
	 * @param {string} event
	 */
	sendEvent(testName, event) {
		const userId = this.getUserId();

		if (!userId || !testName || !event) {
			return;
		}

		navigator.sendBeacon(
			`abtests/event/${this._encodePathPart(testName)}/${this._encodePathPart(event)}/${this._encodePathPart(userId)}/`
		);

		navigator.sendBeacon(
			`abtests/event/${this._encodePathPart(testName)}/${this._encodePathPart(`${event}_${getDevType()}`)}/${this._encodePathPart(userId)}/`
		);
	}

	_sendActiveVariationsImpressions() {
		if (!this._isWasabiEnabled()) {
			return;
		}

		const possibleTestVariationsData = this.getPossibleTestVariationsData(),
		      testNamesNeedImpressions   = this.getActiveTestVariations()
											.split(TEST_VARIATIONS_SEPARATOR)
											.filter(Boolean)
											.map((variationName) => possibleTestVariationsData[variationName])
											.filter((variationData) => variationData && variationData.impressionOnPageLoad)
											.map((variationData) => variationData.test)
											.filter((testName, index, testNames) => (
												!this.sentImpressionTestNames.has(testName) && testNames.indexOf(testName) === index
											));

		if (testNamesNeedImpressions.length) {
			this.sendImpression(testNamesNeedImpressions.join(TEST_VARIATIONS_SEPARATOR));
			testNamesNeedImpressions.forEach((testName) => this.sentImpressionTestNames.add(testName));
		}
	}

	/**
	 * @param {!Array<string>} variationNames
	 * @returns {boolean}
	 */
	isAnyVariationActive(variationNames) {
		return variationNames.some(this.isVariationActive.bind(this));
	}

	/**
	 * @param {string} variationName
	 * @returns {boolean}
	 */
	isVariationActive(variationName) {
		return this.getActiveTestVariations()
				.split(TEST_VARIATIONS_SEPARATOR)
				.includes(variationName);
	}

	/**
	 * Returns the immutable active variations resolved for the current page.
	 * Throws while the assignment is pending so consumers cannot silently treat pending as no assignment.
	 * @returns {string}
	 */
	getActiveTestVariations() {
		this.init();

		if (!this.areAssignmentsResolved) {
			throw new Error('A/B test assignments are not ready');
		}

		return [...this.activeVariations].join(TEST_VARIATIONS_SEPARATOR);
	}

	/** @returns {string} */
	getActiveTestVariationsIfReady() {
		this.init();

		return this.areAssignmentsResolved
			? [...this.activeVariations].join(TEST_VARIATIONS_SEPARATOR)
			: '';
	}

	/**
	 * @param {boolean} usePosthogAssignments
	 * @private
	 */
	_resolveAssignments(usePosthogAssignments) {
		if (this.areAssignmentsResolved) {
			return;
		}

		const possibleTestVariationsData   = this.getPossibleTestVariationsData(),
		      possibleTestVariationsByTest = this._getPossibleTestVariationsByTest(),
		      resolvedByTest               = {},
		      experimentAssignments        = {};

		if (this._isWasabiEnabled()) {
			this._getWasabiActiveTestVariationNames()
				.filter((variation) => !!possibleTestVariationsData[variation])
				.forEach((variation) => {
					resolvedByTest[possibleTestVariationsData[variation].test] = variation;
				});
		}

		if (usePosthogAssignments) {
			// Reading a flag emits exposure unless deferred until display. Only evaluate tests that
			// PostHog can actually assign; Wasabi decisions take precedence.
			const posthogTests = Object.fromEntries(
				Object.entries(possibleTestVariationsByTest).filter(([testName]) => !resolvedByTest[testName])
			);

			Object.entries(this._resolvePosthogAssignments(posthogTests))
				.forEach(([testName, {variationName, featureFlagValue}]) => {
					resolvedByTest[testName] = variationName;
					experimentAssignments[testName] = featureFlagValue;
				});
		}

		const deferredExposureTests = Object.entries(possibleTestVariationsByTest)
										.filter(([, variations]) => this._isExposureDeferred(variations))
										.map(([testName]) => testName),
		      pageExperimentAssignments = Object.fromEntries(
			      Object.keys(possibleTestVariationsByTest).map((testName) => [testName, experimentAssignments[testName]])
		      );

		posthogService.setExperimentAssignments(pageExperimentAssignments, deferredExposureTests);
		this.activeVariations = new Set(Object.values(resolvedByTest));
		this.areAssignmentsResolved = true;

		try {
			this._sendActiveVariationsImpressions();
		} catch (err) {
			console.error(err);
		}
	}

	/** @returns {string[]} @private */
	_getWasabiActiveTestVariationNames() {
		return getValidUpRefCookie().t;
	}

	/** @returns {boolean} @private */
	_isWasabiEnabled() {
		return !!window.globalParams.wasabiEnabled;
	}

	/** @returns {TestVariationsByName} this data came from the backend */
	getPossibleTestVariationsData() {
		return window.globalParams.possibleTestVariations;
	}

	/** @returns {boolean} */
	hasUserId() {
		return !!this.getUserId();
	}

	/** @returns {string} */
	getUserId() {
		return posthogService.getDistinctId() || getUpRefUid();
	}

	/**
	 * @param {!Object<string, string[]>} possibleTestVariationsByTest
	 * @returns {Object<string, PosthogAssignment>}
	 * @private
	 */
	_resolvePosthogAssignments(possibleTestVariationsByTest) {
		/** @type {Object<string, PosthogAssignment>} */
		const assignmentsByTest = {};

		Object.entries(possibleTestVariationsByTest).forEach(([testName, variationNames]) => {
			const sendExposure = !this._isExposureDeferred(variationNames),
			      featureFlagValue = posthogService.getServerLoadedFeatureFlag(testName, {sendExposure}),
			      variationName    = this._getVariationNameFromFeatureFlagValue(featureFlagValue, variationNames);

			if (variationName && !!featureFlagValue) {
				assignmentsByTest[testName] = {variationName, featureFlagValue};
			}
		});

		return assignmentsByTest;
	}

	/**
	 * The server's _getTestVariationConfigData copies test-level exposureOnDisplay
	 * to every variation. These non-empty groups come from _getPossibleTestVariationsByTest.
	 * @param {string[]} variationNames
	 * @returns {boolean}
	 * @private
	 */
	_isExposureDeferred(variationNames) {
		return !!this.getPossibleTestVariationsData()[variationNames[0]].exposureOnDisplay;
	}

	/** @returns {!Object<string, string[]>} @private */
	_getPossibleTestVariationsByTest() {
		const possibleTestVariationsData   = this.getPossibleTestVariationsData(),
		      possibleTestVariationsByTest = {};

		Object.entries(possibleTestVariationsData).forEach(([variationName, variationData]) => {
			if (!variationData || typeof variationData.test !== 'string' || !variationData.test) {
				throw new TypeError(`A/B test variation "${variationName}" must reference a test`);
			}

			if (!possibleTestVariationsByTest[variationData.test]) {
				possibleTestVariationsByTest[variationData.test] = [];
			}

			possibleTestVariationsByTest[variationData.test].push(variationName);
		});

		return possibleTestVariationsByTest;
	}

	/**
	 * @param {boolean|string|undefined} featureFlagValue
	 * @param {!Array<string>} variationNames
	 * @returns {string}
	 * @private
	 */
	_getVariationNameFromFeatureFlagValue(featureFlagValue, variationNames) {
		if (variationNames.includes(featureFlagValue)) {
			return featureFlagValue;
		}

		if (featureFlagValue === 'control') {
			return this._getOriginalVariationName(variationNames);
		}

		if (featureFlagValue === true || featureFlagValue === 'test' || featureFlagValue === 'variant') {
			return this._getNonOriginalVariationName(variationNames);
		}

		return '';
	}

	/**
	 * @param {!Array<string>} variationNames
	 * @returns {string}
	 * @private
	 */
	_getOriginalVariationName(variationNames) {
		const possibleTestVariationsData = this.getPossibleTestVariationsData();

		return variationNames.find((variationName) => possibleTestVariationsData[variationName].isOriginal) || '';
	}

	/**
	 * @param {!Array<string>} variationNames
	 * @returns {string}
	 * @private
	 */
	_getNonOriginalVariationName(variationNames) {
		const possibleTestVariationsData = this.getPossibleTestVariationsData();

		return variationNames.find((variationName) => !possibleTestVariationsData[variationName].isOriginal) || '';
	}

	/**
	 * @param {string} pathPart
	 * @returns {string}
	 * @private
	 */
	_encodePathPart(pathPart) {
		return encodeURIComponent(pathPart);
	}
}

export default new AbTestService();